<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref, watch } from 'vue';
import { currentNotice, dismissNotice, loadNotices } from '../catalog';
import { locale, t } from '../i18n';
import Icon from './Icon.vue';

/**
 * The news banner from the admin panel (TZ 6): a full-width bar at the top of the app that moves the
 * interface down (it never covers the canvas or the panels). Colored by its kind, with "Learn more"
 * and a close button. Asked for when the app opens and then every hour. Its height is published as
 * `--banner-h` for the few fixed elements (the menu drawer) that are placed from the top.
 */
const now = ref(Date.now());
const notice = computed(() => (void now.value, currentNotice(now.value)));
const text = computed(() => {
  const n = notice.value;
  if (!n) return '';
  return (locale.value === 'ru' ? n.text.ru || n.text.en : n.text.en || n.text.ru) ?? '';
});
const ICON = { info: 'info', warning: 'warning', important: 'warning' } as const;
const bar = ref<HTMLElement | null>(null);
let timers: ReturnType<typeof setInterval>[] = [];
let observer: ResizeObserver | null = null;

function publishHeight(): void {
  document.documentElement.style.setProperty('--banner-h', `${bar.value?.offsetHeight ?? 0}px`);
}

watch(bar, (el, old) => {
  if (old) observer?.unobserve(old);
  if (el) observer?.observe(el);
  publishHeight();
});

onMounted(() => {
  observer = new ResizeObserver(publishHeight);
  void loadNotices(true);
  timers = [
    setInterval(() => void loadNotices(false), 3_600_000),
    // the end date passes without a request
    setInterval(() => (now.value = Date.now()), 60_000),
  ];
});
onUnmounted(() => {
  timers.forEach(clearInterval);
  observer?.disconnect();
  document.documentElement.style.setProperty('--banner-h', '0px');
});
</script>

<template>
  <div v-if="notice && text" ref="bar" class="notice-banner" :class="notice.level" role="status" :data-notice="notice.id">
    <div class="inner">
      <Icon :name="ICON[notice.level]" :size="16" class="ico" />
      <span class="txt">{{ text }}</span>
      <a v-if="notice.link" class="more" :href="notice.link" target="_blank" rel="noopener noreferrer">{{ t('notice.more') }}</a>
    </div>
    <button class="close" :aria-label="t('notice.close')" :title="t('notice.close')" data-act="close-notice" @click="dismissNotice(notice.id)"><Icon name="close" :size="15" /></button>
  </div>
</template>

<style scoped>
.notice-banner {
  position: relative;
  flex: none;
  z-index: 950;
  display: flex;
  align-items: center;
  min-height: 40px;
  padding: 6px 48px;
  background: var(--accent-soft);
  color: var(--accent-text);
  border-bottom: 1px solid color-mix(in srgb, var(--accent) 22%, transparent);
  font-size: 13px;
}
.notice-banner.warning {
  background: var(--warn-soft);
  color: var(--warn);
  border-bottom-color: color-mix(in srgb, var(--warn) 25%, transparent);
}
.notice-banner.important {
  background: var(--danger);
  color: #fff;
  border-bottom-color: transparent;
}
.inner {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  min-width: 0;
  flex-wrap: wrap;
  text-align: center;
}
.ico {
  flex: none;
}
.txt {
  color: var(--text);
  line-height: 1.4;
}
.important .txt {
  color: inherit;
}
.more {
  flex: none;
  font-weight: 600;
  color: inherit;
  text-underline-offset: 2px;
}
.close {
  position: absolute;
  right: 10px;
  top: 50%;
  transform: translateY(-50%);
  display: grid;
  place-items: center;
  width: 28px;
  height: 28px;
  border: none;
  border-radius: 999px;
  background: transparent;
  color: inherit;
  opacity: 0.75;
  cursor: pointer;
}
.close:hover {
  opacity: 1;
  background: color-mix(in srgb, currentColor 14%, transparent);
}
@media (max-width: 600px) {
  .notice-banner {
    padding: 6px 44px 6px 14px;
  }
  .inner {
    justify-content: flex-start;
    text-align: left;
  }
}
</style>
